import React from "react";
import { Link } from "gatsby";
import { Avatar, Paper, Typography, Box } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";

const SmallText = styled(Typography)(() => ({
	// fontSize: "86%",
}));

const ProfilePicture = styled(Avatar)(({ theme }) => ({
	marginBottom: theme.spacing(1),
	width: theme.spacing(12),
	height: theme.spacing(12),
	fontSize: "1.3em",
	"&.pic-format-square": {
		borderRadius: 0,
	},
	"&.size-sm": {
		width: theme.spacing(6),
		height: theme.spacing(6),
	},
	"&.size-md": {
		width: theme.spacing(8),
		height: theme.spacing(8),
	},
}));

const HeadingText = styled(Typography)(({ theme }) => ({
	fontWeight: theme.typography.fontWeightBold,
	fontSize: "1.4em",
	"& a": {
		color: theme.palette.link.main,
		textDecoration: "none",
		borderBottom: "1px dotted transparent",
		"&:focus, &:active, &:hover": {
			color: theme.palette.link.hover,
			// textDecoration: "underline",
			borderBottomColor: theme.palette.primary.dark,
		},
	},
	"&.type-company": {
		fontSize: "1.1em",
	},
	"&.type-inline": {
		fontSize: "1em",
		fontWeight: theme.typography.fontWeightRegular,
	},
}));

function ContactCard({
	picture,
	initials,
	title,
	title_append,
	slug,
	jobTitle,
	companyName,
	phone,
	mail,
	website,
	website_label = "Visit website",
	address,
	headingType = "h2",
	cardType = "user",
	tableMode,
	captions,
	pic_format = "rounded",
	imageSize = "lg",
	label,
	isSidebar,
}) {
	const theme = useTheme();
	function Wrapper({ children }) {
		if (tableMode || isSidebar) {
			return (
				<Box
					className="contact-card table-mode"
					sx={{
						display: "flex",
						marginTop: 0,
						display: "block",
						textAlign: "center",
						"& > div ": {
							textAlign: "left",
						},
					}}
				>
					{children}
				</Box>
			);
		} else {
			return (
				<Paper
					elevation={1}
					className={`contact-card type-${cardType} ${
						typeof picture !== "undefined" && picture !== null ? "has-logo" : ""
					}`}
					sx={{
						padding: theme.spacing(2),
						minHeight: 160,
						border: `${theme.palette.grey.dark} 1px solid`,
						"&.type-user": {
							padding: theme.spacing(3, 2),
							textAlign: "center",
						},
						"& .MuiAvatar-root": {
							marginLeft: "auto",
							marginRight: "auto",
						},
						"&.type-company.has-logo": {
							display: "flex",
							alignItems: "center",
							[theme.breakpoints.down("sm")]: {
								display: "block",
								textAlign: "center",
							},
						},
					}}
				>
					{children}
				</Paper>
			);
		}
	}

	function Pic() {
		let attrs = {};
		if (typeof picture !== "undefined" && picture) {
			if (typeof picture === "string") {
				attrs.src = picture;
			} else {
				attrs.src = picture.src;
				attrs.alt = picture.alt;
			}
			if (cardType === "user") {
				if (typeof slug !== "undefined") {
					attrs.component = Link;
					attrs.to = slug;
				}
				return (
					<ProfilePicture
						{...attrs}
						className={`pic-format-${pic_format} size-${imageSize}`}
					>
						{typeof initials !== "undefined" ? initials : ""}
					</ProfilePicture>
				);
			} else {
				function LogoWrap({ children }) {
					return (
						<Box
							sx={{
								"& img": {
									width: 140,
									height: "auto",
									marginRight: theme.spacing(2.5),
									marginLeft: theme.spacing(0.5),
									[theme.breakpoints.down("sm")]: {
										margin: 0,
										width: "80%",
										maxWidth: 140,
									},
									"&.is-sidebar": {
										marginY: theme.spacing(2),
										display: "block",
										width: "100%",
										maxWidth: theme.spacing(20),
									},
								},
							}}
						>
							{typeof slug !== "undefined" ? (
								<Link to={slug}>{children}</Link>
							) : (
								<>{children}</>
							)}
						</Box>
					);
				}
				return (
					<LogoWrap>
						<img
							src={attrs.src}
							alt={attrs.alt}
							className={`${isSidebar ? "is-sidebar" : ""}`}
						/>
					</LogoWrap>
				);
			}
		} else {
			if (cardType === "user") {
				return (
					<ProfilePicture
						className={`pic-format-${pic_format} size-${imageSize}`}
					>
						{typeof initials !== "undefined" ? initials : ""}
					</ProfilePicture>
				);
			} else {
				return <></>;
			}
		}
	}

	function Name() {
		if (!title) return <></>;
		return typeof slug === "undefined" ? (
			<HeadingText component={headingType} className={`type-${cardType}`}>
				<span>{title}</span>
				{title_append && <small>{`, ${title_append}`}</small>}
			</HeadingText>
		) : (
			<HeadingText component={headingType} className={`type-${cardType}`}>
				<Link to={`${slug}`}>
					{title}
					{title_append && <small>{`, ${title_append}`}</small>}
				</Link>
			</HeadingText>
		);
	}

	function Tagline() {
		let tagline = `${typeof jobTitle !== "undefined" ? jobTitle : ""}${
			typeof companyName !== "undefined"
				? `${typeof jobTitle !== "undefined" ? " - " : ""}${companyName}`
				: ""
		}`;
		if (tagline.length > 0)
			return (
				<SmallText>
					<em>{tagline}</em>
				</SmallText>
			);
		return <></>;
	}

	function Address() {
		let address_string = "";
		if (address) {
			if (address.street) address_string += `${address.street} `;
			if (address.city) address_string += `<br /> ${address.city}`;
			if (address.state) address_string += `, ${address.state} `;
			if (address.zip) address_string += ` ${address.zip} `;
		}
		if (address_string.length > 0)
			return (
				<SmallText>
					<span dangerouslySetInnerHTML={{ __html: address_string }}></span>
				</SmallText>
			);
		return <></>;
	}

	function Contact() {
		let contact = [];
		if (typeof mail !== "undefined")
			contact.push(
				<a key={mail} href={`mailto:${mail}`} rel="noreferrer">
					{mail}
				</a>
			);
		if (
			typeof phone !== "undefined" &&
			typeof mail !== "undefined" &&
			!isSidebar
		) {
			contact.push(<span key={`${mail}-sep`}>{" | "}</span>);
		}
		if (typeof phone !== "undefined") {
			if (isSidebar) {
				contact.push(
					<>
						<br />
						<span key={phone}>{phone}</span>
					</>
				);
			} else {
				contact.push(<span key={phone}>{phone}</span>);
			}
		}
		if (contact.length > 0)
			return (
				<SmallText
					className="contact-text"
					component={cardType === "inline" ? "span" : "p"}
				>
					{contact}
				</SmallText>
			);
		return <></>;
	}

	function Website() {
		if (typeof website !== "undefined") {
			return (
				<SmallText>
					<a href={`${website}`} target="_blank" rel="noreferrer">
						{website_label ? website_label : website}
					</a>
				</SmallText>
			);
		}
		return <></>;
	}

	return (
		<Wrapper>
			<Pic />
			<Box
				sx={{
					"& a": {
						color: theme.palette.link.main,
						"&:hover, &:active, &:focus": {
							color: theme.palette.link.hover,
							textDecoration: "none",
						},
					},
				}}
			>
				{label && <Typography variant="button">{`${label} `}</Typography>}
				<Name />
				<Tagline />
				<Address />
				<Contact />
				<Website />
			</Box>
		</Wrapper>
	);
}

export default ContactCard;
